import React from 'react';
import { Cell } from 'react-table';

import { IconTrash } from '@trqk/components';
import { locale } from '@trqk/utils';

import { rowHeight } from '../constants';
import { useToast, useGapAnalysisTools } from '../../../../../../hooks';
import { ConfirmGapAnalysisDeletion } from '../../../../../toastMessages';

interface DeleteCellProps {
    cell: Cell<Record<string, unknown>, GapSummaryReportNameCellValue>;
}

/**
 * Displays a button allowing the user to delete a gap analysis report in the
 * GapAnalysisReportSummaryTable. Upon clicking the button, the user sees a confirmation message
 * requiring them to click "Delete" or "Cancel".
 * @param props - {@link DeleteCellProps}
 * @returns JSX
 */
export function DeleteCell({ cell }: DeleteCellProps): JSX.Element {
    const {
        value: { gapAnalysisId, description },
    } = cell;
    const { add: addToast } = useToast();
    const { deleteGapAnalysis } = useGapAnalysisTools();

    return (
        <div className="flex items-center" style={{ height: rowHeight }}>
            <button
                type="button"
                onClick={() => {
                    addToast({
                        kind: 'warning-card',
                        RenderContent: ConfirmGapAnalysisDeletion,
                        renderContentInput: {
                            gapAnalysisId,
                            description,
                            deleteGapAnalysis,
                        },
                        persist: true,
                        position: 'center',
                        onRemove: (input?: RemoveToastInput): void => {
                            const success = input?.success ?? false;
                            const canceled = input?.canceled ?? false;

                            if (!success && !canceled) {
                                addToast({
                                    kind: 'warning-banner',
                                    content:
                                        locale.enUS.TOASTS
                                            .CONFIRM_GAP_ANALYSIS_DELETION
                                            .ERROR,
                                    logMessage: 'deleteGapAnalysis',
                                });
                            } else if (success && !canceled) {
                                addToast({
                                    kind: 'info-banner',
                                    content:
                                        locale.enUS.TOASTS
                                            .CONFIRM_GAP_ANALYSIS_DELETION
                                            .SUCCESS,
                                });
                            }
                        },
                    });
                }}
            >
                <IconTrash width={24} fillColor="primary-500" />
            </button>
        </div>
    );
}
